import { createContext, useContext, useState, useEffect, useCallback, useRef, type ReactNode } from "react";
import { supabase } from "@/integrations/supabase/client";
import { trackSessionLocation } from "@/lib/geolocation";
import type { User, Session } from "@supabase/supabase-js";

type JwtPayload = { iat?: number };

interface ProfessionalRole {
  roleType: string;
  approvalStatus: string;
  businessName: string | null;
  avatarUrl: string | null;
}

interface Profile {
  firstName: string | null;
  lastName: string | null;
  email: string | null;
  avatarUrl: string | null;
  organizerAvatarUrl: string | null;
  accountType: string | null;
  professionalType: string | null;
  approvalStatus: string | null;
  rejectionReason: string | null;
  isAlsoPromoter: boolean;
  professionalRoles: ProfessionalRole[];
  emailVerifiedAt: string | null;
}

interface AuthState {
  user: User | null;
  session: Session | null;
  profile: Profile | null;
  isLoading: boolean;
  isAdmin: boolean;
}

interface AuthContextValue extends AuthState {
  signOut: () => Promise<void>;
  refetch: () => Promise<void>;
}

const defaultState: AuthState = {
  user: null,
  session: null,
  profile: null,
  isLoading: true,
  isAdmin: false,
};

const AuthContext = createContext<AuthContextValue | null>(null);

const clearSupabaseStorage = () => {
  try {
    const keys: string[] = [];
    for (let i = 0; i < localStorage.length; i++) {
      const k = localStorage.key(i);
      if (!k) continue;
      if (k.startsWith("sb-") || k.includes("supabase")) keys.push(k);
    }
    keys.forEach((k) => localStorage.removeItem(k));
  } catch { /* ignore */ }
  try { sessionStorage.clear(); } catch { /* ignore */ }
};

async function withTimeout<T>(promiseOrThenable: PromiseLike<T>, ms: number): Promise<T | null> {
  return Promise.race([
    Promise.resolve(promiseOrThenable),
    new Promise<null>((resolve) => setTimeout(() => resolve(null), ms)),
  ]);
}

// Try to read cached session from localStorage for instant hydration
const getCachedSession = (): { user: User; session: Session } | null => {
  try {
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (!key || !key.startsWith("sb-") || !key.endsWith("-auth-token")) continue;
      const raw = localStorage.getItem(key);
      if (!raw) continue;
      const parsed = JSON.parse(raw);
      const session = parsed as Session;
      if (session?.user?.id && session?.access_token) {
        return { user: session.user, session };
      }
    }
  } catch { /* ignore */ }
  return null;
};

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const cached = useRef(getCachedSession());
  const [state, setState] = useState<AuthState>(() => {
    // If we have a cached session, start with user populated and isLoading true
    // This lets pages render immediately with user context while we validate
    if (cached.current) {
      return {
        user: cached.current.user,
        session: cached.current.session,
        profile: null,
        isLoading: true, // still loading profile
        isAdmin: false,
      };
    }
    return defaultState;
  });
  const isMounted = useRef(true);
  const stateRef = useRef(state);
  const hasInitialized = useRef(false);
  const loadingStartTime = useRef<number | null>(null);
  const broadcastChannel = useRef<BroadcastChannel | null>(null);
  const isReceivingBroadcast = useRef(false);

  useEffect(() => {
    stateRef.current = state;
  }, [state]);

  // Global safety net: no matter which code path put us in a loading state
  // (init, token refresh, cross-tab sync), never stay there forever. Phones
  // freeze timers in background tabs, so we also re-check whenever the tab
  // becomes visible again instead of relying on the timer alone.
  useEffect(() => {
    if (!state.isLoading) {
      loadingStartTime.current = null;
      return;
    }
    if (loadingStartTime.current === null) loadingStartTime.current = Date.now();

    const stop = () => {
      if (!isMounted.current) return;
      setState((prev) => (prev.isLoading ? { ...prev, isLoading: false } : prev));
    };

    const limitMs = isStripeConnectFlowActive() ? 90_000 : 20_000;
    const timer = window.setTimeout(stop, limitMs);

    const onWake = () => {
      if (document.visibilityState !== "visible") return;
      const started = loadingStartTime.current;
      if (started && Date.now() - started > 10_000) stop();
    };
    document.addEventListener("visibilitychange", onWake);
    window.addEventListener("focus", onWake);
    window.addEventListener("pageshow", onWake);

    return () => {
      window.clearTimeout(timer);
      document.removeEventListener("visibilitychange", onWake);
      window.removeEventListener("focus", onWake);
      window.removeEventListener("pageshow", onWake);
    };
  }, [state.isLoading]);


  const isStripeConnectFlowActive = () => {
    try {
      const params = new URLSearchParams(window.location.search);
      if (params.get("stripe")) return true;
      const startedAt = sessionStorage.getItem("stripe_connect_started_at");
      if (!startedAt) return false;
      const ts = Number(startedAt);
      if (!Number.isFinite(ts)) return false;
      return Date.now() - ts < 15 * 60 * 1000;
    } catch { return false; }
  };

  const decodeJwtPayload = (jwt: string): JwtPayload | null => {
    try {
      const parts = jwt.split(".");
      if (parts.length < 2) return null;
      const base64 = parts[1].replace(/-/g, "+").replace(/_/g, "/");
      const pad = base64.length % 4;
      const padded = pad ? base64 + "=".repeat(4 - pad) : base64;
      return JSON.parse(atob(padded)) as JwtPayload;
    } catch { return null; }
  };

  const fetchGlobalLogoutAt = async (): Promise<number | null> => {
    try {
      const query = supabase
        .from("app_settings")
        .select("value")
        .eq("key", "global_logout_at")
        .maybeSingle();
      const result = await withTimeout(query.then((r) => r), 3000);
      if (!result || result.error || !result.data?.value) return null;
      const ts = (result.data.value as { ts?: number | string })?.ts;
      const numeric = typeof ts === "string" ? Number(ts) : ts;
      return Number.isFinite(numeric) ? (numeric as number) : null;
    } catch { return null; }
  };

  const forceCleanState = useCallback(() => {
    clearSupabaseStorage();
    if (isMounted.current) {
      setState({ user: null, session: null, profile: null, isLoading: false, isAdmin: false });
    }
  }, []);

  const fetchProfileAndAdmin = async (user: User) => {
    try {
      // Single RPC call replaces 4 separate queries
      const result = await withTimeout(
        supabase.rpc('get_auth_user_data', { _user_id: user.id }).then((r: any) => r),
        5000
      );

      if (!result) {
        console.error("Auth user data request timed out");
        return { profile: null, isAdmin: false };
      }

      if (result.error || !result.data) {
        console.error("Error fetching auth user data:", result.error ?? "No auth user data returned");
        return { profile: null, isAdmin: false };
      }

      const data = result.data as any;
      const p = data.profile;
      const professionalRoles: ProfessionalRole[] = (data.professional_roles || []).map((r: any) => ({
        roleType: r.role_type,
        approvalStatus: r.approval_status,
        businessName: r.business_name,
        avatarUrl: r.avatar_url ?? null,
      }));

      return {
        profile: p ? {
          firstName: p.first_name,
          lastName: p.last_name,
          email: p.email,
          avatarUrl: p.avatar_url,
          organizerAvatarUrl: p.organizer_avatar_url ?? null,
          accountType: p.account_type,
          professionalType: p.professional_type,
          approvalStatus: p.approval_status,
          rejectionReason: p.rejection_reason,
          isAlsoPromoter: !!data.is_promoter,
          professionalRoles,
          emailVerifiedAt: p.email_verified_at,
        } : null,
        isAdmin: !!data.is_admin,
      };
    } catch (error) {
      console.error("Error fetching profile:", error);
      return { profile: null, isAdmin: false };
    }
  };

  const syncAuthState = useCallback(async (user: User, session: Session) => {
    try {
      const [globalLogoutAt, profileResult] = await Promise.all([
        fetchGlobalLogoutAt(),
        fetchProfileAndAdmin(user),
      ]);

      if (!isMounted.current) return;

      const activeUserId = stateRef.current.user?.id;
      if (activeUserId && activeUserId !== user.id) return;

      const sessionIat = session.access_token ? decodeJwtPayload(session.access_token)?.iat : undefined;
      if (globalLogoutAt && sessionIat && sessionIat < globalLogoutAt) {
        console.warn("Session predates global logout; signing out");
        forceCleanState();
        await supabase.auth.signOut().catch(() => {});
        return;
      }

      setState({ user, session, profile: profileResult.profile, isLoading: false, isAdmin: profileResult.isAdmin });
      trackSessionLocation(user.id);
    } catch (error) {
      console.error("Error fetching profile on auth change:", error);
      if (isMounted.current && stateRef.current.user?.id === user.id) {
        setState(prev => ({ ...prev, profile: null, isLoading: false, isAdmin: false }));
      }
    }
  }, [forceCleanState]);

  useEffect(() => {
    isMounted.current = true;

    // Set up cross-tab auth sync via BroadcastChannel
    try {
      const channel = new BroadcastChannel("nightspy-auth");
      broadcastChannel.current = channel;
      channel.onmessage = (event) => {
        if (!isMounted.current) return;
        const { type, userId } = event.data || {};
        if (type === "SIGNED_OUT") {
          setState({ user: null, session: null, profile: null, isLoading: false, isAdmin: false });
          return;
        }
        if (type === "SESSION_UPDATED" && userId) {
          // Another tab refreshed the token - grab the fresh session from storage
          // instead of trying to refresh our own (now-revoked) token.
          // The flag must always be released, even if getSession never settles
          // (common when a phone freezes a background tab), otherwise this tab
          // would ignore every later auth event and hang on the loader.
          isReceivingBroadcast.current = true;
          const release = window.setTimeout(() => { isReceivingBroadcast.current = false; }, 5000);
          const done = () => { window.clearTimeout(release); isReceivingBroadcast.current = false; };
          supabase.auth.getSession().then(({ data: { session } }) => {
            done();
            if (!isMounted.current || !session?.user) return;
            setState(prev => {
              if (prev.user?.id !== session.user.id) return prev;
              return { ...prev, session, isLoading: prev.profile ? false : prev.isLoading };
            });
          }).catch(done);
        }
        if (type === "TAB_ACTIVE") {
          // Another tab is now the one in use: let it own token refreshes so
          // suspended background tabs never race it and stall on the loader.
          void supabase.auth.stopAutoRefresh().catch(() => {});
        }
      };
    } catch { /* BroadcastChannel not supported - graceful fallback */ }

    // The most recently opened tab becomes the session leader.
    const claimLeadership = () => {
      if (document.visibilityState !== "visible") return;
      void supabase.auth.startAutoRefresh().catch(() => {});
      try { broadcastChannel.current?.postMessage({ type: "TAB_ACTIVE" }); } catch {}
    };
    claimLeadership();
    document.addEventListener("visibilitychange", claimLeadership);
    window.addEventListener("pageshow", claimLeadership);
    window.addEventListener("focus", claimLeadership);

    const initializeAuth = async () => {
      if (hasInitialized.current) return;
      hasInitialized.current = true;
      loadingStartTime.current = Date.now();

      const stripeFlowActive = isStripeConnectFlowActive();
      const watchdogMs = stripeFlowActive ? 90_000 : 15_000;
      const getSessionTimeoutMs = stripeFlowActive ? 30_000 : 4_000;

      // If we have a cached user, start fetching profile immediately in parallel
      // with getSession validation - don't wait for getSession to complete first
      const cachedUser = cached.current?.user;
      const earlyProfilePromise = cachedUser ? fetchProfileAndAdmin(cachedUser) : null;

      const watchdog = window.setTimeout(() => {
        console.warn("Auth initialization watchdog triggered - stopping loading");
        if (isMounted.current) setState((prev) => ({ ...prev, isLoading: false }));
      }, watchdogMs);

      try {
        // If we have a cached user, resolve profile ASAP without waiting for getSession
        if (earlyProfilePromise) {
          earlyProfilePromise.then(result => {
            if (!isMounted.current) return;
            setState(prev => {
              // Only apply if we still have the same user and haven't already resolved
              if (prev.user?.id === cachedUser!.id && !prev.profile) {
                return { ...prev, profile: result.profile, isAdmin: result.isAdmin, isLoading: false };
              }
              return prev;
            });
          }).catch(() => {});
        }

        const sessionResult = await withTimeout(supabase.auth.getSession(), getSessionTimeoutMs);

        if (!sessionResult) {
          console.warn(`getSession timed out after ${getSessionTimeoutMs}ms - stopping loading`);
          window.clearTimeout(watchdog);
          // If we already resolved from cache, don't disrupt
          if (isMounted.current) setState((prev) => ({ ...prev, isLoading: false }));
          return;
        }

        const { data: { session }, error: sessionError } = sessionResult;
        window.clearTimeout(watchdog);

        if (sessionError) {
          console.warn("Session error during initialization:", sessionError.message);
          forceCleanState();
          await supabase.auth.signOut().catch(() => {});
          return;
        }

        const user = session?.user ?? null;
        if (!isMounted.current) return;

        if (!user) {
          setState({ user: null, session: null, profile: null, isLoading: false, isAdmin: false });
          return;
        }

        // If same user as cache, we may already have profile from early fetch
        let profileResult: { profile: Profile | null; isAdmin: boolean };
        if (cachedUser?.id === user.id && earlyProfilePromise) {
          profileResult = await earlyProfilePromise;
        } else {
          profileResult = await fetchProfileAndAdmin(user);
        }

        const [globalLogoutAt] = await Promise.all([
          fetchGlobalLogoutAt(),
        ]);

        const sessionIat = session?.access_token ? decodeJwtPayload(session.access_token)?.iat : undefined;
        if (globalLogoutAt && sessionIat && sessionIat < globalLogoutAt) {
          console.warn("Session predates global logout; signing out");
          forceCleanState();
          await supabase.auth.signOut().catch(() => {});
          return;
        }

        if (isMounted.current) {
          setState({ user, session, profile: profileResult.profile, isLoading: false, isAdmin: profileResult.isAdmin });
          trackSessionLocation(user.id);
          // Auto-verify OAuth users or users who confirmed via email link.
          // Direct profiles UPDATE is blocked by RLS (email_verified_at is locked),
          // so use the security-definer RPC which re-checks auth.users server-side.
          if (profileResult.profile && !profileResult.profile.emailVerifiedAt) {
            const isOAuth = user.app_metadata?.provider && user.app_metadata.provider !== 'email';
            const created = new Date(user.created_at).getTime();
            const confirmed = user.email_confirmed_at ? new Date(user.email_confirmed_at).getTime() : 0;
            const confirmedManually = confirmed > 0 && (confirmed - created) > 60000;
            if (isOAuth || confirmedManually) {
              (supabase.rpc as any)('mark_email_verified_if_confirmed').then(({ data: ok }: any) => {
                if (ok && isMounted.current) {
                  setState(prev => prev.profile ? { ...prev, profile: { ...prev.profile, emailVerifiedAt: new Date().toISOString() } } : prev);
                }
              });
            }
          }

        }
      } catch (error) {
        window.clearTimeout(watchdog);
        console.error("Error initializing auth:", error);
        forceCleanState();
        await supabase.auth.signOut().catch(() => {});
      }
    };

    const handleVisibilityChange = async () => {
      if (document.visibilityState !== "visible") return;
      if (!isMounted.current) return;
      const stripeFlowActive = isStripeConnectFlowActive();
      const stuckThresholdMs = stripeFlowActive ? 90_000 : 30_000;
      setState((current) => {
        if (current.isLoading && hasInitialized.current && loadingStartTime.current) {
          const elapsed = Date.now() - loadingStartTime.current;
          if (elapsed > stuckThresholdMs) {
            console.warn(`Stuck loading for ${elapsed}ms on return - stopping loading`);
            return { ...current, isLoading: false };
          }
        }
        return current;
      });
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);

    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      const currentSession = session ?? null;
      const user = currentSession?.user ?? null;
      if (!isMounted.current) return;

      // Skip heavy processing if this tab is just receiving a broadcast sync
      if (isReceivingBroadcast.current) return;

      if (event === 'INITIAL_SESSION') return;

      if (event === 'TOKEN_REFRESHED' && !currentSession) {
        console.warn("Token refresh failed, signing out");
        forceCleanState();
        return;
      }

      if (event === 'SIGNED_OUT' || !user || !currentSession) {
        setState({ user: null, session: null, profile: null, isLoading: false, isAdmin: false });
        try { broadcastChannel.current?.postMessage({ type: "SIGNED_OUT" }); } catch {}
        return;
      }

      // Broadcast session update to other tabs so they don't race to refresh
      if (event === 'TOKEN_REFRESHED' || event === 'SIGNED_IN') {
        try { broadcastChannel.current?.postMessage({ type: "SESSION_UPDATED", userId: user.id }); } catch {}
      }

      const currentState = stateRef.current;
      if (currentState.user?.id === user.id && currentState.profile && !currentState.isLoading) {
        setState(prev => ({ ...prev, user, session: currentSession }));
        return;
      }

      setState(prev => ({ ...prev, user, session: currentSession, isLoading: true }));

      window.setTimeout(() => {
        void syncAuthState(user, currentSession);
      }, 0);
    });

    initializeAuth();

    const handleRefetchRequest = () => {
      setTimeout(async () => {
        const { data: { user } } = await supabase.auth.getUser();
        if (user && isMounted.current) {
          const result = await fetchProfileAndAdmin(user);
          if (isMounted.current) {
            setState(prev => ({ ...prev, profile: result.profile, isAdmin: result.isAdmin, isLoading: false }));
          }
        }
      }, 100);
    };

    window.addEventListener("auth-refetch", handleRefetchRequest);

    return () => {
      isMounted.current = false;
      subscription.unsubscribe();
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      document.removeEventListener("visibilitychange", claimLeadership);
      window.removeEventListener("pageshow", claimLeadership);
      window.removeEventListener("focus", claimLeadership);
      window.removeEventListener("auth-refetch", handleRefetchRequest);
      try { broadcastChannel.current?.close(); } catch {}
    };
  }, [forceCleanState, syncAuthState]);

  const signOut = useCallback(async () => {
    try { broadcastChannel.current?.postMessage({ type: "SIGNED_OUT" }); } catch {}
    forceCleanState();
    const localSignOut = async () => {
      try {
        await (supabase.auth.signOut as any)({ scope: "local" });
      } catch {
        try { await supabase.auth.signOut(); } catch { /* ignore */ }
      }
    };
    await Promise.race([localSignOut(), new Promise<void>((resolve) => setTimeout(resolve, 2000))]);
  }, [forceCleanState]);

  const refetch = useCallback(async () => {
    if (!state.user) return;
    setState(prev => ({ ...prev, isLoading: true }));
    try {
      const { profile, isAdmin } = await fetchProfileAndAdmin(state.user);
      if (isMounted.current) {
        setState(prev => ({ ...prev, profile, isAdmin, isLoading: false }));
      }
    } catch (error) {
      console.error("Error refetching profile:", error);
      if (isMounted.current) setState(prev => ({ ...prev, isLoading: false }));
    }
  }, [state.user]);

  return (
    <AuthContext.Provider value={{ ...state, signOut, refetch }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    // Fallback for components outside provider - return safe defaults
    return {
      user: null,
      session: null,
      profile: null,
      isLoading: true,
      isAdmin: false,
      signOut: async () => {},
      refetch: async () => {},
    };
  }
  return context;
};
